import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { yieldToMain } from '@wix/da-hooks/pkg/yieldToMain';
import { useBodyScrollLock } from '@wix/da-ds/pkg/hooks/useBodyScrollLock';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import { getIsUserDropdownOpen } from '../../../redux/selectors';
import { closeNotificationsDropdown } from '../../../redux/actions';

interface UserMenuContextValue {
  isOpen: boolean;
  isSliding: boolean;
  setScrollableRef: (ref: any) => void;
  toggleUserMenu: () => void;
}

export const UserMenuContext = React.createContext<UserMenuContextValue>({
  isOpen: false,
  isSliding: false,
  setScrollableRef: () => null,
  toggleUserMenu: () => null,
});

const UserMenuContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const reduxIsUserMenuOpen = useSelector(getIsUserDropdownOpen);
  const dispatchCloseNotificationsDropdown = useAction(closeNotificationsDropdown);
  const wasOpen = useRef(false);

  const [isUserMenuOpen, setIsUserMenuOpen] = useState(!!reduxIsUserMenuOpen);
  const [isSliding, setIsSliding] = useState(false);
  const { onLock, onUnlock, setScrollableRef } = useBodyScrollLock();

  useEffect(() => {
    const isOpenChanged = wasOpen.current !== reduxIsUserMenuOpen;
    if (isOpenChanged && isUserMenuOpen !== reduxIsUserMenuOpen) {
      setIsUserMenuOpen(!!reduxIsUserMenuOpen);
    }

    wasOpen.current = !!reduxIsUserMenuOpen;
  }, [reduxIsUserMenuOpen, isUserMenuOpen]);

  const openUserMenu = useCallback(() => {
    onLock();
    setIsSliding(true);

    yieldToMain(() => {
      dispatchCloseNotificationsDropdown();
      setIsUserMenuOpen(true);
      setIsSliding(false);
    });
  }, [onLock, dispatchCloseNotificationsDropdown]);

  const closeUserMenu = useCallback(() => {
    onUnlock();
    setIsSliding(true);
    yieldToMain(() => {
      setIsUserMenuOpen(false);
      setIsSliding(false);
    });
  }, [onUnlock]);

  const toggleUserMenu = useCallback(
    () => (isUserMenuOpen ? closeUserMenu() : openUserMenu()),
    [closeUserMenu, isUserMenuOpen, openUserMenu]
  );

  const contextValue: UserMenuContextValue = useMemo(
    () => ({
      isOpen: isUserMenuOpen,
      isSliding,
      setScrollableRef,
      toggleUserMenu,
    }),
    [setScrollableRef, isSliding, isUserMenuOpen, toggleUserMenu]
  );

  return <UserMenuContext.Provider value={contextValue}>{children}</UserMenuContext.Provider>;
};
UserMenuContextProvider.displayName = 'UserMenuContextProvider';

export { UserMenuContextProvider };
